import { useEffect } from 'react';

interface Handler {
  key: string;
  callback: (event: KeyboardEvent) => void;
}

// Shared registry of handlers
const handlers = new Set<Handler>();

// Single global listener that delegates to all registered handlers
function globalKeydownHandler(event: KeyboardEvent) {
  const target = event.target as HTMLElement;

  if (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable ||
    event.metaKey ||
    event.ctrlKey ||
    event.altKey
  ) {
    return;
  }

  for (const handler of handlers) {
    if (handler.key === event.key) {
      handler.callback(event);
    }
  }
}

// Lazily attach/detach the global listener based on whether any handlers are registered
function ensureGlobalListener() {
  if (handlers.size === 1) {
    window.addEventListener('keydown', globalKeydownHandler);
  }
}
function cleanupGlobalListener() {
  if (handlers.size === 0) {
    window.removeEventListener('keydown', globalKeydownHandler);
  }
}

/**
 * Listens for a keydown event when the user is not focused on an input or textarea.
 * This is useful for listening for general keyboard shortcuts, like [ and ] to toggle the app sidebars.
 *
 * Uses a shared global listener pattern - only one event listener is attached to the window,
 * regardless of how many components use this hook.
 *
 * Also see: workspaces/shared-frontend/hooks/useKeydown
 */
export default function useUnfocusedKeydown(
  key: string,
  callback: (event: KeyboardEvent) => void,
  isEnabled: boolean = true
) {
  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const handler: Handler = { key, callback };
    handlers.add(handler);
    ensureGlobalListener();

    return () => {
      handlers.delete(handler);
      cleanupGlobalListener();
    };
  }, [key, callback, isEnabled]);
}
